import React from 'react';
import axios from 'axios';
import { Box, Card, CardContent, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useToken from '../../services/token';
import UsersManagementTab from './UsersManagementTab';
import RolesManagementTab from './RolesManagementTab';
import SpectralMonitoringTab from './SpectralMonitoringTab';
function StatCard({ icon, value, label, color }) {
    return (React.createElement(Card, { variant: "outlined" },
        React.createElement(CardContent, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, py: '12px !important' } },
            React.createElement(Box, { sx: { color: color !== null && color !== void 0 ? color : 'rgb(18, 113, 71)' } }, icon),
            React.createElement(Box, null,
                React.createElement(Typography, { variant: "h6", fontWeight: 700, sx: { color: color !== null && color !== void 0 ? color : 'inherit', lineHeight: 1 } }, value),
                React.createElement(Typography, { variant: "caption", color: "text.secondary" }, label)))));
}
export default function AdminPageContent({ token }) {
    var _a, _b, _c;
    const { parseJwt } = useToken();
    const payload = React.useMemo(() => parseJwt(token), [token]);
    const role = (_a = payload === null || payload === void 0 ? void 0 : payload.role) !== null && _a !== void 0 ? _a : null;
    const currentUserId = (_b = payload === null || payload === void 0 ? void 0 : payload.id) !== null && _b !== void 0 ? _b : null;
    const isAdmin = role === 'admin';
    const [users, setUsers] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const load = React.useCallback(() => {
        if (!isAdmin) {
            setLoading(false);
            return;
        }
        setLoading(true);
        Promise.all([
            axios.get('/user/all-with-details', { headers: { Authorization: `Bearer ${token}` } }),
            axios.get('/roles/list?limit=50', { headers: { Authorization: `Bearer ${token}` } }),
        ]).then(([usersRes, rolesRes]) => {
            var _a, _b, _c;
            setUsers((_a = usersRes.data) !== null && _a !== void 0 ? _a : []);
            setRoles((_c = (_b = rolesRes.data) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : []);
        }).finally(() => setLoading(false));
    }, [token, isAdmin]);
    React.useEffect(() => { load(); }, [load]);
    const pending = users.filter((u) => u.status === 'pending');
    const tabs = React.useMemo(() => {
        const list = [];
        if (isAdmin) {
            list.push({ key: 'all', label: `Все пользователи (${users.length})` });
            list.push({ key: 'pending', label: `Заявки (${pending.length})` });
            list.push({ key: 'roles', label: 'Роли' });
            list.push({ key: 'spectral', label: 'Снимки' });
        }
        return list;
    }, [isAdmin, users.length, pending.length]);
    const [tab, setTab] = React.useState(0);
    const currentKey = (_c = tabs[tab]) === null || _c === void 0 ? void 0 : _c.key;
    if (tabs.length === 0) {
        return (React.createElement(Box, { sx: { p: 3 } },
            React.createElement(Typography, { color: "text.secondary" }, "\u0423 \u0432\u0430\u0441 \u043D\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F\u0430 \u043A \u044D\u0442\u043E\u043C\u0443 \u0440\u0430\u0437\u0434\u0435\u043B\u0443.")));
    }
    return (React.createElement(Stack, { spacing: 0, sx: { flex: '1 1 auto', pt: '0px' } },
        React.createElement(Box, { sx: { p: { xs: 2, md: 3 } } },
            React.createElement(Typography, { component: "h1", variant: "h6", fontWeight: 600, sx: { mb: 1.5 } }, "\u0410\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u0435"),
            isAdmin && (React.createElement(Grid, { container: true, spacing: 2, sx: { mb: 3, maxWidth: 720 } },
                React.createElement(Grid, { item: true, xs: 4 },
                    React.createElement(StatCard, { icon: React.createElement(PeopleIcon, null), value: users.length, label: "\u0412\u0441\u0435\u0433\u043E \u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u0435\u0439" })),
                React.createElement(Grid, { item: true, xs: 4 },
                    React.createElement(StatCard, { icon: React.createElement(HourglassEmptyIcon, null), value: pending.length, label: "\u041E\u0436\u0438\u0434\u0430\u044E\u0442 \u043E\u0434\u043E\u0431\u0440\u0435\u043D\u0438\u044F", color: "#8a6d1a" })),
                React.createElement(Grid, { item: true, xs: 4 },
                    React.createElement(StatCard, { icon: React.createElement(CheckCircleIcon, null), value: users.filter(u => u.status === 'active').length, label: "\u0410\u043A\u0442\u0438\u0432\u043D\u044B\u0445", color: "#2e7d32" })))),
            React.createElement(Tabs, { value: tab, onChange: (_, v) => setTab(v), sx: { mb: 2, borderBottom: '1px solid #eaeaea' } }, tabs.map((t) => React.createElement(Tab, { key: t.key, label: t.label }))),
            (currentKey === 'all' || currentKey === 'pending') && (React.createElement(UsersManagementTab, { token: token, currentUserId: currentUserId, users: currentKey === 'pending' ? pending : users, roles: roles, loading: loading, onReload: load, mode: currentKey === 'pending' ? 'pending' : 'all' })),
            currentKey === 'roles' && React.createElement(RolesManagementTab, { token: token, roles: roles, onReload: load }),
            currentKey === 'spectral' && React.createElement(SpectralMonitoringTab, { token: token }))));
}
